// The Givmo company suite's MODULE REGISTRY — the square contract (EP-SUITE3, D-EP-15; shell
// contract v3 at OM-14).
//
// ⚠ THIS FILE IS DUPLICATED BYTE-FOR-BYTE IN `apps/admin/src/modules/module-registry.ts`, and a test
// in each app pins the two identical. Apps in this workspace do not import each other, and a package
// for one data module would carry its own package.json / tsconfig / eslint / vitest and enter every
// turbo task graph for ~120 lines of const — so the house trade is a copy plus a byte-identity pin.
// EDIT BOTH COPIES, or the pin reds.
//
// WHAT A SQUARE IS. The nine-dot popup in every company-suite portal's chrome is the person's APP
// LIST, rendered as "app-store" squares. This module is the ONE list of what a square can be: its
// slug, how it is named to a person, and the build-time variable naming the host that serves it. The
// authoring contract — how a program registers a new square — is written up in
// `agent_docs/org-console-shell.md`.
//
// TWO GATES, and only one of them lives here:
//   1. IS IT BUILT? — a module renders ONLY when its `VITE_*` URL variable is SET for this build.
//      That is what this file decides, and there is deliberately no default anywhere: an unbuilt
//      portal has no URL, so it cannot render a square — which is how "no decorative controls" is
//      held structurally rather than by remembering. Every module URL is ENV-CONFIG an operator
//      sets per environment, never a flag with a guessed default.
//   2. WHAT STATE IS IT IN? — absent, off for the organization, setting up, locked to this admin, or
//      an ordinary link. That RULE is not here: it is `deriveConsoleSquares` in `@givmo/org-console`,
//      the ONE square derivation every host's popup and landing consume, written once so a second
//      copy cannot be a second answer to "may this person open that". Both v3 flags below —
//      `classDefault` and `adminConsole` — are DATA that derivation reads; the registry no longer
//      composes the rule itself (the app-level `applyModuleAccess` retired with the suite home).
//
// ⚠ HOME IS NOT A THING (D-CPO-31, R2). The suite-home `console` square is GONE — no grid tile, no
// sidebar row, no breadcrumb root. What replaced it is the ADMIN CONSOLE row at the end of this list:
// the organization's own management pages (`/org`), presented as a tile that renders only for a
// holder of one of its pages' capabilities, LAST and badged with the pending-request count. It is
// not a backend module — the catalog reports no standing for it — which is why the derivation reads
// it from the caller's capabilities rather than from the standing map.
//
// ⚠ `VITE_COMPANY_CONSOLE_URL` STAYS, as the console ORIGIN variable the pair reads. It is no longer a
// square's URL: the Admin Console row's destination is the console origin PLUS `/org`, composed by
// `registryTable` from whichever origin the host hands in — the live origin for the console itself,
// this variable for a portal on another host. Unset ⇒ nothing composed, never a guess.
//
// UNKNOWN SLUGS ARE IGNORED BY CONSTRUCTION: the registry is the closed set, so an env var naming a
// module nobody registered resolves to nothing rather than to a mystery tile.
//
// A MODULE MAY BE A PATH IN THIS SAME CONSOLE (OM-12), and the registry carries that as DATA: an
// optional `path`, present only once a module's own mount lands. The mechanics a path-mounted module
// needs — which module a URL is inside, where its square goes, whether someone may open it on a
// direct link, its own sign-in door, and the sidebar its pages wear — are written ONCE in
// `@givmo/org-console` over a host-supplied list; `mountedModuleRoutes` below is how this registry
// hands that list in. Nothing about the two gates changes: a module still renders only where its URL
// variable is set, whichever origin serves it.

import {
  Briefcase,
  Building2,
  FolderKanban,
  Gavel,
  HeartHandshake,
  Landmark,
  Megaphone,
  ScrollText,
  Share2,
  Tags,
  type LucideProps,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { isMountedInThisBuild, type ConsoleTableRow, type ModuleRoute } from '@givmo/org-console';

/** A Lucide (or Lucide-compatible) icon component, matching the design system's `LucideLikeIcon`. */
export type ModuleIcon = ComponentType<LucideProps>;

/**
 * The closed set of module slugs. A square's slug is an e2e selector — never rename one in place.
 *
 * ⚠ `org` is the Admin Console row, not a module: it is here because a square needs a slug
 * (`module-square-org`), and the registry is the one closed list of squares.
 */
export type ModuleSlug =
  | 'employer'
  | 'campaigns'
  | 'sales_gifting'
  | 'fundraisers'
  | 'books'
  | 'compliance'
  | 'board'
  | 'projects'
  | 'social'
  | 'org';

export interface ModuleDefinition {
  slug: ModuleSlug;
  /** The module's name as a person would say it. */
  label: string;
  /** One short line about what the module is for. */
  description: string;
  icon: ModuleIcon;
  /**
   * The build-time variable naming the origin that serves this module.
   *
   * For the Admin Console row it is the console ORIGIN variable — read by a portal on another host
   * to compose the tile's destination; the console itself composes from its live origin instead.
   */
  urlEnvVar: string;
  /**
   * The in-console path this module is served at — present only once a build MOUNTS it (OM-12).
   *
   * ⚠ RESERVED IS NOT MOUNTED, and the absence is the honest entry. A module gains this row on the
   * day its own mount lands, together with a URL variable naming this console's origin plus this
   * path. Until both exist, "mounted in this build" cannot be true, and every consumer of it — the
   * square's destination, the per-module door's vocabulary, the direct-link gate, the sidebar's
   * module family — answers "not here" by construction instead of by remembering to.
   *
   * ⚠ THE PATH IS NOT THE SLUG. The slug is the backend's product vocabulary and an e2e selector;
   * the path is what a person reads in the address bar, and the two differ deliberately. The
   * published rows are the table in `agent_docs/org-console-shell.md`.
   */
  path?: string;
  /**
   * The class's DEFAULT LANDING module — exactly ONE per registry (shell contract v3, T1.4).
   *
   * Where a person lands when nothing more specific applies: no chosen default, no last-module
   * hint, more than one openable app. Its destination is whatever the ONE resolver answers for it,
   * exactly as for any tile — an unmounted or unconfigured class default is unopenable and the
   * landing rule falls through past it. For the company class that is the Donations module, the
   * `employer` row, mounted at `/donations` on the console (Employer Portal EP-MNT4): in-app where
   * `VITE_EMPLOYER_PORTAL_URL` names this console's own `/donations`, the portal's host where it
   * names that, nothing where it is unset.
   */
  classDefault?: true;
  /**
   * The organization's own management tile — the ADMIN CONSOLE — at most ONE per registry (T4.1).
   *
   * Not a backend module: the catalog reports no standing for it. It renders iff the caller's
   * SETTLED capability list carries one of its pages' capabilities (never an `isGlobalAdmin`
   * check), always as a link, LAST, badged with the pending-request count, and it never counts as an
   * openable app (R1). Its `path` is the console's own `/org`.
   */
  adminConsole?: true;
}

/**
 * Every square the suite knows about, in display order. The order is the popup's order, and it is a
 * judgement rather than an accident: the portals a customer buys first, then the back office. The
 * Admin Console row sits last in the list AND is moved last by the derivation (D2), so the two
 * cannot disagree.
 */
export const MODULE_REGISTRY: readonly ModuleDefinition[] = [
  // The company's Donations module — "Donations" is its name on the tile, the bar and the Apps page
  // (D-EP-48 ruling 1; the slug stays `employer`) — mounted at `/donations` (EP-MNT4).
  {
    slug: 'employer',
    label: 'Donations',
    description: 'Matching and gifting',
    icon: Briefcase,
    urlEnvVar: 'VITE_EMPLOYER_PORTAL_URL',
    path: '/donations',
    classDefault: true,
  },
  {
    slug: 'campaigns',
    label: 'Business',
    description: 'Campaigns and fundraising',
    icon: Megaphone,
    urlEnvVar: 'VITE_BUSINESS_PORTAL_URL',
  },
  {
    slug: 'sales_gifting',
    label: 'Sales',
    description: 'Gifting to prospects',
    icon: Tags,
    urlEnvVar: 'VITE_SALES_PORTAL_URL',
  },
  {
    slug: 'fundraisers',
    label: 'Fundraisers',
    description: 'Fundraisers and campaign teams',
    icon: HeartHandshake,
    urlEnvVar: 'VITE_FUNDRAISERS_MODULE_URL',
    path: '/fundraisers',
  },
  // The three back-office modules. They are registered here rather than hidden, because the popup is
  // the same component for a GivFi operator and for a customer, and the operator needs them.
  //
  // ⚠ NOT OFFERED TO CUSTOMER ORGANIZATIONS TODAY, AND THAT IS DATA RATHER THAN A PROMISE. The
  // backend's product vocabulary does not model them, so no standing is reported and the state rule
  // omits them — the ordinary `absent` path, not a special case. D-COB-2 was always "until we
  // affirmatively open it", and these three are on their way to being offered to companies: when the
  // catalog starts reporting them they become ordinary tiles with no change here. Do not write
  // "never" about them, and do not build anything that assumes the current answer is fixed.
  {
    slug: 'books',
    label: 'Books',
    description: 'Ledger and close',
    icon: Landmark,
    urlEnvVar: 'VITE_COMPANY_BOOKS_URL',
    path: '/books',
  },
  {
    slug: 'compliance',
    label: 'Compliance',
    description: 'Filings and registrations',
    icon: ScrollText,
    urlEnvVar: 'VITE_COMPANY_COMPLIANCE_URL',
  },
  {
    slug: 'board',
    label: 'Board',
    description: 'Meetings and minutes',
    icon: Gavel,
    urlEnvVar: 'VITE_COMPANY_BOARD_URL',
  },
  // The Project Management module (PMM-WEB1): every plan of the organization, mounted at
  // `/projects` where its variable names this console. Offered by data: the backend withholds it
  // from every organization but those its offering row names, so the square is absent elsewhere.
  {
    slug: 'projects',
    label: 'Projects',
    description: 'Plans and progress',
    icon: FolderKanban,
    urlEnvVar: 'VITE_COMPANY_PROJECTS_URL',
    path: '/projects',
  },
  // Social (SMM-WEB1): the organization's posts to its own pages and accounts, mounted at `/social`
  // where its variable names this console. No switch (D-SMM-14): the variable names where the module
  // is served, never whether it shows — the backend's verdict does, so an organization not offered
  // Social has no square.
  {
    slug: 'social',
    label: 'Social',
    description: 'Posts to your pages and accounts',
    icon: Share2,
    urlEnvVar: 'VITE_COMPANY_SOCIAL_URL',
    path: '/social',
  },
  // The organization's own management pages as a tile — see the header and `adminConsole`.
  {
    slug: 'org',
    label: 'Admin Console',
    description: 'Apps, team, activity',
    icon: Building2,
    urlEnvVar: 'VITE_COMPANY_CONSOLE_URL',
    path: '/org',
    adminConsole: true,
  },
];

/** The Vite env record the URL variables are read from (`import.meta.env` in production). */
export type RegistryEnv = Record<string, string | boolean | undefined>;

/**
 * Read a `VITE_*` value as a trimmed non-empty string, or `undefined`.
 *
 * ⚠ THERE IS NO FALLBACK PARAMETER, and its absence is the contract. Every module URL is
 * ENV-CONFIG — a value an operator sets per environment — never a flag with a sensible default. A
 * per-env default map here would mean an unset variable silently resolves to a GUESSED origin, and
 * a square pointing at a guessed host is worse than no square: it is a working-looking link into
 * nothing, in a product whose whole grid rule is that a tile is either real or absent.
 */
export function readUrl(env: RegistryEnv, name: string): string | undefined {
  const raw = env[name];
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Join a configured origin and an in-app path without doubling or dropping the separator.
 *
 * An operator who writes the origin with a trailing slash is not making a mistake — it is how half
 * the world writes one — but the naive concatenation then produces a doubled separator, which some
 * hosts serve and some redirect. Normalising here fixes every caller at once; doing it at each call
 * site is how one of them gets missed.
 */
export function joinPath(origin: string, path: string | undefined): string {
  const trimmed = (path ?? '').trim();
  if (trimmed.length === 0) return origin;
  return `${origin.replace(/\/+$/, '')}/${trimmed.replace(/^\/+/, '')}`;
}

/**
 * One registry row as the shared derivation reads it: a {@link ConsoleTableRow} (slug · path · the
 * configured destination · the two v3 flags) that keeps the display fields, so a host maps a square
 * straight to its tile without a second lookup.
 */
export interface RegistryRow extends ConsoleTableRow, Omit<ModuleDefinition, 'path'> {
  slug: ModuleSlug;
}

/**
 * The registry as the ONE console table `@givmo/org-console` derives every square from.
 *
 * Gate 1 is applied here: a module's `href` is its URL variable's value, verbatim, or `undefined`
 * when the variable is unset — and the derivation renders NOTHING for a row with no address, in
 * every state, which is what keeps an unconfigured build from producing a control that goes nowhere.
 *
 * ⚠ THE ADMIN CONSOLE ROW COMPOSES ITS DESTINATION FROM `adminConsoleOrigin`, NOT FROM ITS VARIABLE.
 * The console itself hands in its LIVE origin (it serves `/org`; a variable about its own address
 * would be the guessed default this file forbids); a portal on another host hands in the value of
 * `VITE_COMPANY_CONSOLE_URL`, or `null` when unset — and then there is no tile, never a guess.
 */
export function registryTable(env: RegistryEnv, adminConsoleOrigin: string | null): RegistryRow[] {
  return MODULE_REGISTRY.map(({ path, ...definition }) => {
    const href =
      definition.adminConsole === true
        ? adminConsoleOrigin === null
          ? undefined
          : joinPath(adminConsoleOrigin, path)
        : readUrl(env, definition.urlEnvVar);
    return {
      ...definition,
      ...(path === undefined ? {} : { path }),
      ...(href === undefined ? {} : { href }),
    };
  });
}

/** The class default's slug — exactly one row carries the flag, pinned by the registry test. */
export const CLASS_DEFAULT_SLUG: ModuleSlug = 'employer';

/**
 * The registry as the shared path-module substrate reads it (OM-12): one route per MODULE this build
 * actually MOUNTS — an address here AND a configured destination.
 *
 * ⚠ IT LIVES IN THIS FILE SO THE BYTE-IDENTITY PIN COVERS IT. The derivation is registry data plus
 * one variable read; in a per-app file the two suites could disagree about which modules are mounted
 * while their registries still matched byte for byte, which is the drift the pin exists to catch.
 *
 * ⚠ ONLY THE MOUNTED SET COMES BACK, and that is what makes it the list every consumer wants. A row
 * with an address but no configured destination is not served here: its route is not registered, its
 * segment is not in the sign-in vocabulary, and a typed URL reaches the not-found surface — the same
 * answer the tile's absence gives, by the same gate-1 rule.
 *
 * ⚠ THE ADMIN CONSOLE ROW IS NOT A MODULE and is never in this list: its `/org` pages are the
 * console's own, declared by the shell, and a per-module sign-in door or module chrome for them
 * would be a door onto the organization's management pages wearing a product's name.
 *
 * ⚠ AND THE ORIGIN IS NOT AN INPUT HERE. Whether a destination belongs to this build is decided at
 * CALL time against the live origin (`moduleHref`), never here: one bundle is deployed to several
 * environments under different hostnames, so an origin spelled in this file would be exactly the
 * guessed default the rail above forbids.
 */
export interface RegisteredModuleRoute extends ModuleRoute {
  /** Narrowed to the closed set, so a host can hand the answer straight back to the registry. */
  slug: ModuleSlug;
}

export function mountedModuleRoutes(env: RegistryEnv): RegisteredModuleRoute[] {
  const routes: RegisteredModuleRoute[] = [];
  for (const definition of MODULE_REGISTRY) {
    if (definition.path === undefined || definition.adminConsole === true) continue;
    const route: RegisteredModuleRoute = {
      slug: definition.slug,
      path: definition.path,
      href: readUrl(env, definition.urlEnvVar),
    };
    if (isMountedInThisBuild(route)) routes.push(route);
  }
  return routes;
}
